import React, { useState, useEffect } from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, MdFilterList
} from './ThemeLayoutHelpers';
import { MdMenu, MdClose, MdArrowForward, MdOutlineCircle, MdShield, MdAutoAwesome } from 'react-icons/md';

export default function SilkyClayLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#c97a60"; // Terracotta
  const secondary = T.secondaryColor || "#e0c0b0"; // Desert sand/beige
  const text = p.storefrontText || {};
  
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeMineralTab, setActiveMineralTab] = useState("bentonite");

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copy = getHeroContent(p, {
    eyebrow: "Mineral Earth Radiance",
    title: "Silky Minerals. Natural Finishes.",
    subtitle: "Purify and sculpt your skin matrix with mineral-rich clay deposits and organic earth tones engineered for absolute balance.",
    primaryCta: "Shop Minerals",
    secondaryCta: "Our Sourcing",
  });

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "shop") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fbf9f6] text-[#443834] antialiased" style={{ fontFamily: T.fontFamily || "DM Sans, sans-serif" }}>
      {/* Terracotta top banner */}
      <div className="w-full bg-[#f2ebe5] border-b border-[#e1d3c7] py-2 px-4 text-center text-xs font-semibold tracking-wide text-[#7d655a]">
        <span>🏺 MINERAL ORIGINS: Buy 2 clay masks, get an organic clay mixing bowl free. </span>
        <button onClick={(e) => navigateTo("shop", e)} className="underline hover:text-black ml-1 inline-flex items-center gap-0.5">
          Claim offer <MdArrowForward className="text-[10px]" />
        </button>
      </div>

      {/* Minimalist Nav */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#fbf9f6]/95 border-b border-[#e1d3c7] shadow-sm" 
          : "bg-transparent border-b border-transparent"
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border border-[#e1d3c7]" size="h-9 w-9" />
            <div>
              <p className="text-base font-bold tracking-tight text-[#443834] tracking-wider uppercase font-serif">{p.storeInfo?.businessName}</p>
              <p className="text-[9px] text-[#c97a60] tracking-widest font-black uppercase mt-0.5">{p.storeInfo?.businessCategory || "Mineral Cosmetics"}</p>
            </div>
          </div>

          {/* Links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["shop", "Shop Clay"],
              ["details", "Origins"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`text-xs font-bold tracking-widest uppercase transition-colors hover:text-[#c97a60] ${
                  activePage === page 
                    ? "text-[#c97a60] border-b border-[#c97a60] pb-0.5" 
                    : "text-slate-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3.5">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="hidden md:block w-48" />
            
            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-lg border border-[#e1d3c7] hover:bg-[#f2ebe5] text-[#443834] transition-colors" title="Wishlist">
              <FaHeart className="text-base text-[#c97a60]" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 text-[9px] font-bold rounded-full text-white flex items-center justify-center bg-[#c97a60] shadow-sm">
                  {p.wishlistCount}
                </span>
              )}
            </button>

            <CartBtn p={p} />
            {p.userMenuElement}

            <button 
              className="md:hidden p-2 rounded-lg border border-[#e1d3c7] text-[#443834]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#fbf9f6] border-b border-[#e1d3c7] shadow-xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="w-full mb-2" />
            {[
              ["home", "Home"],
              ["shop", "Shop Clay"],
              ["details", "Origins"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`py-2 text-left text-xs font-bold tracking-widest uppercase border-b border-gray-100 hover:text-[#c97a60] ${
                  activePage === page ? "text-[#c97a60] font-bold" : "text-slate-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </nav>

      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* Minimal splitscreen Hero */}
            <section className="max-w-6xl mx-auto px-6 pt-6 pb-12">
              <div className="relative rounded-2xl overflow-hidden bg-white border border-[#e1d3c7] shadow-sm p-8 md:p-14 flex flex-col md:flex-row items-center gap-10 min-h-[460px]">
                {/* Left content */}
                <div className="flex-1 text-left relative z-10">
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[#c97a60] mb-3">
                    <MdOutlineCircle className="text-sm text-[#c97a60]" />
                    <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                  </span>
                  <h1 className="text-4xl md:text-5xl font-normal font-serif text-[#443834] leading-tight mb-4 tracking-tight">
                    <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                  </h1>
                  <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-lg">
                    <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                  </p>
                  
                  <div className="flex flex-wrap gap-4">
                    <button 
                      onClick={() => navigateTo("shop")}
                      className="px-8 py-3 rounded-none text-xs font-bold uppercase tracking-wider text-white shadow-md active:scale-98 transition-all"
                      style={{ backgroundColor: primary }}
                    >
                      <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                    </button>
                    <button 
                      onClick={() => { p.setActiveTab("details"); navigateTo("about"); }}
                      className="px-8 py-3 rounded-none text-xs font-bold uppercase tracking-wider text-[#443834] border border-[#e1d3c7] bg-transparent hover:bg-[#fbf9f6] transition-colors"
                    >
                      <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText>
                    </button>
                  </div>
                </div>

                {/* Right image */}
                <div className="flex-1 w-full relative z-10 self-stretch rounded-xl overflow-hidden border border-[#e1d3c7] shadow-inner bg-[#fcfaf9]">
                  <HeroImage p={p} themeId="silkyclay" className="h-[280px] md:h-full">
                    <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-white/10 to-transparent pointer-events-none" />
                  </HeroImage>
                </div>
              </div>
            </section>

            {/* Mineral Spotlight: Bentonite, Kaolin, Zeolite */}
            <section className="max-w-6xl mx-auto px-6 py-12">
              <div className="bg-[#faf6f2] border border-[#e1d3c7] rounded-2xl p-8 md:p-12 shadow-sm text-left">
                <div className="max-w-xl mb-8">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#c97a60]">EARTH SCIENCE</span>
                  <h2 className="text-3xl font-bold font-serif text-[#443834] mt-1">Ethical mud structures.</h2>
                  <p className="text-gray-500 text-xs mt-2">Discover the raw mineral foundations we source from active thermal deposits to purify pores and restore skin balance.</p>
                </div>

                <div className="flex flex-col md:flex-row gap-6 items-stretch">
                  {/* Selectors */}
                  <div className="flex md:flex-col gap-2 overflow-x-auto pb-3 md:pb-0 border-b md:border-b-0 md:border-r border-[#e1d3c7] w-full md:w-56 shrink-0">
                    {[
                      { id: "bentonite", label: "Volcanic Bentonite" },
                      { id: "kaolin", label: "French White Kaolin" },
                      { id: "zeolite", label: "Activated Zeolite" }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveMineralTab(tab.id)}
                        className={`px-4 py-3 text-xs font-bold text-left tracking-wider uppercase transition-colors ${
                          activeMineralTab === tab.id 
                            ? "bg-white text-[#c97a60] border-l-2 border-[#c97a60]" 
                            : "text-slate-400 hover:text-slate-700"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Descriptions */}
                  <div className="flex-1 bg-white border border-[#e1d3c7] p-6 md:p-8 rounded-xl flex flex-col justify-between">
                    {activeMineralTab === "bentonite" && (
                      <div className="animate-fadeIn font-sans">
                        <h4 className="text-base font-bold font-serif text-[#443834]">Active Wyoming Bentonite</h4>
                        <p className="text-gray-500 text-xs mt-2 leading-relaxed">Highly absorbent volcanic ash clay that acts as a natural magnet. Draws out excess sebum, environmental heavy metals, and micro-particles from deep within pores.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="bg-[#faf6f2] border border-[#e1d3c7] p-4 rounded-lg">
                            <span className="text-2xl font-bold font-serif text-[#c97a60]">Magnet +</span>
                            <p className="text-[10px] text-gray-500 mt-1">Deep Pore Pull</p>
                          </div>
                          <div className="bg-[#faf6f2] border border-[#e1d3c7] p-4 rounded-lg">
                            <span className="text-2xl font-bold font-serif text-[#c97a60]">pH Balance</span>
                            <p className="text-[10px] text-gray-500 mt-1">Alkalizing Skin Guard</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {activeMineralTab === "kaolin" && (
                      <div className="animate-fadeIn font-sans">
                        <h4 className="text-base font-bold font-serif text-[#443834]">French Fine-Grained Kaolin</h4>
                        <p className="text-gray-500 text-xs mt-2 leading-relaxed">The gentlest of all cosmetic clays. Mild mineral compound that exfoliates dead surface cells and stimulates local micro-circulation without stripping essential moisture.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="bg-[#faf6f2] border border-[#e1d3c7] p-4 rounded-lg">
                            <span className="text-2xl font-bold font-serif text-[#c97a60]">Gentle</span>
                            <p className="text-[10px] text-gray-500 mt-1">Dry & Sensitive Skin Safe</p>
                          </div>
                          <div className="bg-[#faf6f2] border border-[#e1d3c7] p-4 rounded-lg">
                            <span className="text-2xl font-bold font-serif text-[#c97a60]">Exfoliate</span>
                            <p className="text-[10px] text-gray-500 mt-1">Natural Silica Polishing</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {activeMineralTab === "zeolite" && (
                      <div className="animate-fadeIn font-sans">
                        <h4 className="text-base font-bold font-serif text-[#443834]">Clinoptilolite Zeolite Mud</h4>
                        <p className="text-gray-500 text-xs mt-2 leading-relaxed">A molecular sieve that exchanges healthy calcium and potassium ions with skin toxins. Provides strong antioxidant defense and reduces thermal skin stress.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="bg-[#faf6f2] border border-[#e1d3c7] p-4 rounded-lg">
                            <span className="text-2xl font-bold font-serif text-[#c97a60]">Antioxidant</span>
                            <p className="text-[10px] text-gray-500 mt-1">Toxin Exchanger</p>
                          </div>
                          <div className="bg-[#faf6f2] border border-[#e1d3c7] p-4 rounded-lg">
                            <span className="text-2xl font-bold font-serif text-[#c97a60]">Ion Guard</span>
                            <p className="text-[10px] text-gray-500 mt-1">Releases Minerals</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Earth value badges */}
            <section className="bg-white border-y border-[#e1d3c7]/50 py-10 px-6">
              <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { title: "Sustainably Mined", icon: <MdShield className="text-2xl text-[#c97a60]" />, desc: "Certified ethical geo-mining" },
                  { title: "Pure Mineral Base", icon: <MdOutlineCircle className="text-2xl text-[#c97a60]" />, desc: "Zero chemical dyes or talc" },
                  { title: "Small Batches", icon: <MdAutoAwesome className="text-2xl text-[#c97a60]" />, desc: "Handcrafted freshness locked" },
                  { title: "Zero Filler Compounds", icon: <MdShield className="text-2xl text-[#c97a60]" />, desc: "100% active earth compounds" }
                ].map((b, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center p-4 border border-[#e1d3c7]/20 rounded bg-[#faf6f2]">
                    <div className="p-3 rounded-none bg-white mb-3 border border-[#e1d3c7] shadow-sm">{b.icon}</div>
                    <p className="text-xs font-bold text-[#443834] uppercase tracking-wider">{b.title}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Catalog */}
        <section id="catalog-section" className="max-w-6xl mx-auto px-6 py-12 scroll-mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div className="text-left">
              <span className="text-xs font-bold uppercase tracking-widest text-[#c97a60]">MINERAL RETAIL</span>
              <h3 className="text-3xl font-bold font-serif text-[#443834] mt-1">Clay Infusions</h3>
            </div>

            {/* Filter controls */}
            <div className="flex items-center gap-2.5">
              <button 
                onClick={() => p.setFilterPanelOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-none border border-[#e1d3c7] bg-white hover:bg-gray-50 text-xs font-bold text-gray-700 transition-colors uppercase tracking-wider"
              >
                <MdFilterList size={16} /> Filter
              </button>
              
              <div className="rounded-none border border-[#e1d3c7] bg-white p-1 flex gap-1">
                {[
                  ["products", getTabLabel(p, "products")],
                  ["services", getTabLabel(p, "services")],
                  ["details", getTabLabel(p, "details")]
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      p.setActiveTab(k);
                      if (k === "details") {
                        navigateTo("about");
                      } else {
                        navigateTo("shop");
                      }
                    }}
                    className="px-4 py-1.5 text-xs font-bold transition-all whitespace-nowrap uppercase tracking-wider"
                    style={p.activeTab === k ? { backgroundColor: primary, color: "#fff" } : { color: "#7d655a" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full">
            <TabContent p={p} />
          </div>
        </section>
      </main>

      <StoreFooter p={p} />
    </div>
  );
}
